// 🔒 DGII-RNC-VALIDATOR-ACCOUNTING-2026-07
// 🔒 DGII-RNC-DUPLICATE-SUPPLIER-ALERT-2026-07 — Extendido para mostrar
//     alerta amber cuando el RNC ya existe en el maestro de proveedores.
import React from 'react';
import { Loader2, Check, X, AlertCircle } from 'lucide-react';

/**
 * Badge visual del estado de validación RNC/Cédula contra DGII.
 *
 * Props:
 *   status: 'idle' | 'invalid_format' | 'checking' | 'valid' | 'not_found' | 'error'
 *   data: object | null (respuesta del endpoint DGII)
 *   documentType: 'RNC' | 'CEDULA'
 *   testIdPrefix: string opcional (para tests: `${prefix}-badge-valid` etc.)
 *   existingEntity: { id, name, trade_name, rnc } | null — si el RNC ya
 *       corresponde a un proveedor existente en el maestro. Se muestra como
 *       badge amber adicional debajo del principal.
 *   onViewEntity: (id) => void | null — callback opcional. Si se provee,
 *       el badge amber renderiza un botón "Ver proveedor →".
 *
 * Retorna null cuando no hay nada relevante que mostrar (idle o formato incompleto).
 */
export default function RncStatusBadge({
  status,
  data,
  documentType,
  testIdPrefix = 'rnc',
  existingEntity = null,
  onViewEntity = null,
}) {
  const duplicateBadge = existingEntity ? (
    <div
      className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300 mt-1 p-1.5 rounded-md bg-amber-500/10 border border-amber-500/30"
      data-testid={`${testIdPrefix}-badge-duplicate`}
    >
      <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate">
          Ya existe como &quot;{existingEntity.name}&quot;
        </div>
        {existingEntity.trade_name && (
          <div className="text-[10px] opacity-80 truncate">
            Nombre comercial: {existingEntity.trade_name}
          </div>
        )}
      </div>
      {onViewEntity && (
        <button
          type="button"
          onClick={() => onViewEntity(existingEntity.id)}
          className="text-[10px] font-medium underline hover:text-amber-800 dark:hover:text-amber-200 flex-shrink-0 min-h-[36px] px-2 -my-1"
          data-testid={`${testIdPrefix}-badge-view-existing`}
        >
          Ver proveedor →
        </button>
      )}
    </div>
  ) : null;

  if (status === 'idle' || status === 'invalid_format') {
    return duplicateBadge;
  }

  if (status === 'checking') {
    return (
      <>
        <div
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1"
          data-testid={`${testIdPrefix}-badge-checking`}
        >
          <Loader2 size={12} className="animate-spin" />
          <span>Consultando DGII…</span>
        </div>
        {duplicateBadge}
      </>
    );
  }

  if (status === 'valid' && data) {
    return (
      <>
        <div
          className="flex items-start gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-300 mt-1 p-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/30"
          data-testid={`${testIdPrefix}-badge-valid`}
        >
          <Check size={12} className="mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold truncate">{data.nombre || 'Sin nombre'}</div>
            {(data.estado || data.actividad_economica) && (
              <div className="text-[10px] opacity-80 truncate">
                {data.estado && <span>{data.estado}</span>}
                {data.actividad_economica && <span> · {data.actividad_economica}</span>}
              </div>
            )}
          </div>
        </div>
        {duplicateBadge}
      </>
    );
  }

  if (status === 'not_found') {
    return (
      <>
        <div
          className="flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-300 mt-1"
          data-testid={`${testIdPrefix}-badge-not-found`}
        >
          <AlertCircle size={12} />
          <span>{documentType} no encontrado en DGII</span>
        </div>
        {duplicateBadge}
      </>
    );
  }

  if (status === 'error') {
    return (
      <>
        <div
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1"
          data-testid={`${testIdPrefix}-badge-error`}
        >
          <X size={12} />
          <span>No se pudo consultar DGII (validación opcional)</span>
        </div>
        {duplicateBadge}
      </>
    );
  }

  return duplicateBadge;
}
